/**
 * 位置情報取得ユーティリティ
 * - 逆ジオコーディング（住所取得）
 * - OSM Overpass（周辺施設取得）
 */

import axios from 'axios'
import muni from '@/js/muni'

/**
 * 単一地点の逆ジオコーディング（国土地理院API）
 * @param {number} lng 経度
 * @param {number} lat 緯度
 * @param {boolean} includePrefecture 都道府県を含めるか（デフォルト: false）
 * @returns {Promise<string|null>} 住所文字列またはnull
 */
export async function reverseGeocode(lng, lat, includePrefecture = false) {
  try {
    const response = await axios.get('https://mreversegeocoder.gsi.go.jp/reverse-geocoder/LonLatToAddress', {
      params: { lon: lng, lat: lat },
    })

    if (response.data.results) {
      const splitMuni = muni[Number(response.data.results.muniCd)]?.split(',')
      if (splitMuni) {
        // 都道府県 + 市区町村 + 町丁目、または市区町村 + 町丁目
        const prefix = includePrefecture ? splitMuni[1] : ''
        return prefix + splitMuni[3] + (response.data.results.lv01Nm || '')
      }
    }
    return null
  } catch (error) {
    console.warn('Reverse geocoding failed:', error)
    return null
  }
}

/**
 * 地図範囲内の複数地点から住所を取得（9点グリッド）
 * @param {Object} bounds MapLibreのLngLatBounds
 * @returns {Promise<string[]>} ユニークな住所の配列
 */
export async function getAddressesInBounds(bounds) {
  const sw = bounds.getSouthWest()
  const ne = bounds.getNorthEast()

  // 3x3グリッド = 9点
  const lngStep = (ne.lng - sw.lng) / 2
  const latStep = (ne.lat - sw.lat) / 2

  const points = []
  for (let i = 0; i <= 2; i++) {
    for (let j = 0; j <= 2; j++) {
      points.push({
        lng: sw.lng + lngStep * i,
        lat: sw.lat + latStep * j,
      })
    }
  }

  // 並列でリクエスト（国土地理院APIは比較的寛容）
  const promises = points.map(p => reverseGeocode(p.lng, p.lat))
  const results = await Promise.all(promises)

  // nullを除外してユニークな住所を返す
  const uniqueAddresses = [...new Set(results.filter(addr => addr !== null))]
  return uniqueAddresses
}

/**
 * OSM Overpassで周辺施設を取得
 * @param {Object} bounds MapLibreのLngLatBounds
 * @param {number} limit 取得件数上限
 * @returns {Promise<Array>} 施設情報の配列
 */
export async function getPOIsInBounds(bounds, limit = 50) {
  const sw = bounds.getSouthWest()
  const ne = bounds.getNorthEast()

  // Overpass QL クエリ
  // 主要な施設タイプを取得
  const query = `
[out:json][timeout:10];
(
  // 商業施設
  node["shop"](${sw.lat},${sw.lng},${ne.lat},${ne.lng});
  way["shop"](${sw.lat},${sw.lng},${ne.lat},${ne.lng});
  // アメニティ（学校、病院、コンビニ等）
  node["amenity"](${sw.lat},${sw.lng},${ne.lat},${ne.lng});
  way["amenity"](${sw.lat},${sw.lng},${ne.lat},${ne.lng});
  // レジャー施設（公園等）
  node["leisure"](${sw.lat},${sw.lng},${ne.lat},${ne.lng});
  way["leisure"](${sw.lat},${sw.lng},${ne.lat},${ne.lng});
  // 観光施設
  node["tourism"](${sw.lat},${sw.lng},${ne.lat},${ne.lng});
  way["tourism"](${sw.lat},${sw.lng},${ne.lat},${ne.lng});
);
out center ${limit};
`

  try {
    const response = await axios.post(
      'https://overpass-api.de/api/interpreter',
      query,
      {
        headers: { 'Content-Type': 'text/plain' },
        timeout: 15000,
      }
    )

    if (!response.data.elements) {
      return []
    }

    // 結果を整形
    const pois = response.data.elements
      .filter(el => el.tags && el.tags.name) // 名前があるもののみ
      .map(el => {
        const tags = el.tags
        // タイプを判定
        let type = ''
        if (tags.shop) type = `shop=${tags.shop}`
        else if (tags.amenity) type = `amenity=${tags.amenity}`
        else if (tags.leisure) type = `leisure=${tags.leisure}`
        else if (tags.tourism) type = `tourism=${tags.tourism}`

        return {
          name: tags.name,
          type: type,
          // 日本語タイプ名
          typeJa: getJapaneseType(tags),
        }
      })
      .slice(0, limit)

    return pois
  } catch (error) {
    console.warn('Overpass API failed:', error)
    return []
  }
}

/**
 * OSMタグから日本語タイプ名を取得
 */
function getJapaneseType(tags) {
  const typeMap = {
    // amenity
    school: '学校',
    kindergarten: '幼稚園',
    hospital: '病院',
    clinic: '診療所',
    pharmacy: '薬局',
    bank: '銀行',
    post_office: '郵便局',
    police: '交番',
    fire_station: '消防署',
    library: '図書館',
    community_centre: '公民館',
    place_of_worship: '宗教施設',
    restaurant: 'レストラン',
    cafe: 'カフェ',
    fast_food: 'ファストフード',
    fuel: 'ガソリンスタンド',
    parking: '駐車場',
    // shop
    supermarket: 'スーパー',
    convenience: 'コンビニ',
    bakery: 'パン屋',
    butcher: '肉屋',
    department_store: 'デパート',
    mall: 'ショッピングモール',
    // leisure
    park: '公園',
    playground: '遊び場',
    sports_centre: 'スポーツ施設',
    swimming_pool: 'プール',
    // tourism
    hotel: 'ホテル',
    museum: '博物館',
    attraction: '観光地',
  }

  const amenity = tags.amenity
  const shop = tags.shop
  const leisure = tags.leisure
  const tourism = tags.tourism

  return typeMap[amenity] || typeMap[shop] || typeMap[leisure] || typeMap[tourism] || '施設'
}

/**
 * 住所と施設情報をまとめて取得
 * @param {Object} bounds MapLibreのLngLatBounds
 * @returns {Promise<{addresses: string[], pois: Array}>}
 */
export async function getLocationInfo(bounds) {
  // 並列で取得
  const [addresses, pois] = await Promise.all([
    getAddressesInBounds(bounds),
    getPOIsInBounds(bounds),
  ])

  return { addresses, pois }
}

/**
 * AI用にフォーマットされた位置情報テキストを生成
 * @param {Object} locationInfo getLocationInfoの戻り値
 * @param {Object} options オプション { includeAddress: boolean, includeFacility: boolean }
 * @returns {string} プロンプト用テキスト
 */
export function formatLocationInfoForAI(locationInfo, options = {}) {
  const { addresses, pois } = locationInfo
  const { includeAddress = true, includeFacility = true } = options

  let text = ''

  if (includeAddress && addresses.length > 0) {
    text += `【この地図に含まれる地域】\n`
    text += addresses.join('、') + '\n\n'
  }

  if (includeFacility && pois.length > 0) {
    text += `【周辺の主な施設】\n`
    pois.forEach(poi => {
      text += `- ${poi.name}（${poi.typeJa}）\n`
    })
  }

  return text
}
